import React, { useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { BuildingIcon, UserIcon, TruckIcon, DollarSignIcon, HandshakeIcon, ShieldIcon, HardHatIcon, SettingsIcon } from 'lucide-react';
export const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [selectedRole, setSelectedRole] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const {
    login
  } = useAuth();
  // Auto-select foreman role on mobile devices
  useEffect(() => {
    // Check if it's likely a mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      setSelectedRole('foreman');
    }
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!selectedRole) {
      setError('Please select a role');
      return;
    }
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    const success = await login(email, password, selectedRole);
    if (!success) {
      setError('Invalid credentials. Please try again.');
    }
    // Save email if remember me is checked
    if (rememberMe && success) {
      localStorage.setItem('rememberedEmail', email);
      localStorage.setItem('rememberedRole', selectedRole);
    } else {
      localStorage.removeItem('rememberedEmail');
      localStorage.removeItem('rememberedRole');
    }
  };
  // Load remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    const rememberedRole = localStorage.getItem('rememberedRole');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
    if (rememberedRole) {
      setSelectedRole(rememberedRole);
    }
  }, []);
  // Main user roles (excluding admin)
  const mainRoles = [{
    id: 'foreman',
    name: 'Foreman',
    icon: <HardHatIcon size={24} data-id="element-42" />,
    primary: true
  }, {
    id: 'contractor',
    name: 'Contractor',
    icon: <BuildingIcon size={24} data-id="element-43" />
  }, {
    id: 'broker',
    name: 'Broker',
    icon: <HandshakeIcon size={24} data-id="element-44" />
  }, {
    id: 'trucking',
    name: 'Trucking Co.',
    icon: <TruckIcon size={24} data-id="element-45" />
  }, {
    id: 'driver',
    name: 'Driver',
    icon: <UserIcon size={24} data-id="element-46" />
  }, {
    id: 'factoring',
    name: 'Factoring',
    icon: <DollarSignIcon size={24} data-id="element-47" />
  }];
  return <div className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center bg-no-repeat" style={{
    backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)), url('https://uploadthingy.s3.us-west-1.amazonaws.com/jcmHr2eoQFt6DpLQsYVeqj/IMG_6388.jpg')`
  }} data-id="element-48">
      <div className="bg-white rounded-lg shadow-md p-5 w-full max-w-md" data-id="element-49">
        <div className="flex justify-center mb-5" data-id="element-50">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/u8SmBgeG73CtBG3qA3KHuY/IMG_6390.jpg" alt="DirtLogics Logo" className="w-64 h-auto" data-id="element-51" />
        </div>
        <div className="flex mb-5" data-id="element-52">
          <button className={`flex-1 py-3 text-center font-medium ${isLogin ? 'bg-[#0F2A52] text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setIsLogin(true)} data-id="element-53">
            Login
          </button>
          <button className={`flex-1 py-3 text-center font-medium ${!isLogin ? 'bg-[#0F2A52] text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setIsLogin(false)} data-id="element-54">
            Register
          </button>
        </div>
        <form onSubmit={handleSubmit} data-id="element-55">
          <div className="mb-5 relative" data-id="element-56">
            <div className="flex justify-between items-center mb-2" data-id="element-57">
              <label className="text-sm font-medium" data-id="element-58">Select Role</label>
              {/* Admin button as a small gear icon */}
              <button type="button" className={`p-2 rounded-full ${selectedRole === 'admin' ? 'bg-[#0F2A52] text-white' : 'text-gray-400 hover:bg-gray-100'}`} onClick={() => setSelectedRole('admin')} aria-label="Admin Login" data-id="element-59">
                <SettingsIcon size={16} data-id="element-60" />
              </button>
            </div>
            {/* Main role grid - 2 rows of 3 roles */}
            <div className="grid grid-cols-3 gap-3" data-id="element-61">
              {mainRoles.map(role => <button key={role.id} type="button" className={`border rounded-md p-3 flex flex-col items-center justify-center transition-colors ${selectedRole === role.id ? 'border-[#0F2A52] bg-[#0F2A52]/10 text-[#0F2A52]' : 'border-gray-200 hover:bg-gray-50'} ${role.primary ? 'bg-[#F58220]/10 border-[#F58220]/30' : ''}`} onClick={() => setSelectedRole(role.id)} data-id="element-62">
                  <div className={`mb-2 ${selectedRole === role.id ? 'text-[#0F2A52]' : role.primary ? 'text-[#F58220]' : 'text-gray-500'}`} data-id="element-63">
                    {role.icon}
                  </div>
                  <div className="text-sm font-medium" data-id="element-64">{role.name}</div>
                </button>)}
            </div>
            {/* Show admin selection indicator if admin is selected */}
            {selectedRole === 'admin' && <div className="mt-2 p-2 bg-[#0F2A52]/10 rounded-md flex items-center justify-center text-sm text-[#0F2A52]" data-id="element-65">
                <ShieldIcon size={16} className="mr-1" data-id="element-66" />
                Admin role selected
              </div>}
          </div>
          <div className="mb-4" data-id="element-67">
            <label className="block text-sm font-medium mb-2" data-id="element-68">
              Email or Username
            </label>
            <input type="text" className="w-full p-3 border border-gray-300 rounded text-base" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email" autoCapitalize="none" autoComplete="username" data-id="element-69" />
          </div>
          <div className="mb-4" data-id="element-70">
            <div className="flex justify-between mb-2" data-id="element-71">
              <label className="block text-sm font-medium" data-id="element-72">Password</label>
              <a href="#" className="text-sm text-[#F58220]" data-id="element-73">
                Forgot password?
              </a>
            </div>
            <input type="password" className="w-full p-3 border border-gray-300 rounded text-base" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" autoComplete="current-password" data-id="element-74" />
          </div>
          <div className="mb-4 flex items-center" data-id="element-75">
            <input type="checkbox" id="rememberMe" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="h-4 w-4 text-[#0F2A52] border-gray-300 rounded" data-id="element-76" />
            <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700" data-id="element-77">
              Remember me
            </label>
          </div>
          {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm" data-id="element-78">
              {error}
            </div>}
          <button type="submit" className="w-full bg-[#0F2A52] hover:bg-[#0F2A52]/90 text-white py-3 rounded-md font-medium text-base" data-id="element-79">
            {isLogin ? 'Sign In' : 'Create Account'}
          </button>
        </form>
      </div>
    </div>;
};